import { useState, useEffect } from "react";
import { Copy, Check, MessageCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const ReferralShare = () => {
  const { user } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [referralUrl, setReferralUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ joined: 0, completed: 0, pointsEarned: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setLoading(true);
      try {
        // Generate or get existing code
        const { data } = await supabase.functions.invoke("referral", {
          body: { action: "generate", userId: user.id },
        });

        if (data?.code) {
          setCode(data.code);
          setReferralUrl(data.referralUrl || `https://flexaura.me?ref=${data.code}`);
        }

        // Fetch referral stats
        const { data: referrals } = await supabase
          .from("referrals")
          .select("status, points_awarded")
          .eq("referrer_user_id", user.id)
          .not("referred_user_id", "is", null);

        const rows = referrals ?? [];
        setStats({
          joined: rows.length,
          completed: rows.filter((r) => r.status === "completed").length,
          pointsEarned: rows.reduce((s, r) => s + (r.points_awarded ?? 0), 0),
        });
      } catch (e) {
        console.error("Failed to load referral data:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const whatsappMessage = encodeURIComponent(
    `I've been using Flexaura to spread the cost of travel, school fees and healthcare across monthly payments. Use my link to get 250 bonus points on your first booking: ${referralUrl}`
  );

  const emailSubject = encodeURIComponent("Join Flexaura — earn 250 bonus points");
  const emailBody = encodeURIComponent(
    `Hey!\n\nI've been using Flexaura to spread the cost of travel, school fees and healthcare across monthly payments.\n\nSign up with my link and you'll get 250 bonus points on your first booking:\n${referralUrl}\n\nSee you there!`
  );

  if (loading) {
    return (
      <Card className="border-2 border-accent/30 bg-primary">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-primary-foreground/10 rounded w-48" />
            <div className="h-4 bg-primary-foreground/10 rounded w-64" />
            <div className="h-10 bg-primary-foreground/10 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-2 border-accent/30 bg-primary text-primary-foreground">
        <CardContent className="p-6 space-y-5">
          <div>
            <h3 className="text-xl font-bold font-serif">Invite friends, earn points</h3>
            <p className="text-primary-foreground/70 text-sm mt-1">
              You earn 500 points. They earn 250 points on their first booking.
            </p>
          </div>

          {/* Code display */}
          <div className="bg-primary-foreground/10 rounded-lg p-3 flex items-center gap-3">
            <code className="font-mono text-sm flex-1 tracking-wider text-accent">
              {referralUrl}
            </code>
          </div>

          {/* Share buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="secondary"
              className="flex-1 gap-2"
              onClick={handleCopy}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy Link"}
            </Button>

            <Button
              variant="secondary"
              className="flex-1 gap-2"
              asChild
            >
              <a
                href={`https://wa.me/?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-4 h-4" />
                Share on WhatsApp
              </a>
            </Button>

            <Button
              variant="secondary"
              className="flex-1 gap-2"
              asChild
            >
              <a href={`mailto:?subject=${emailSubject}&body=${emailBody}`}>
                <Mail className="w-4 h-4" />
                Share via Email
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Mini leaderboard */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground px-1">
        <span>Your referrals:</span>
        <span className="font-medium text-foreground">{stats.joined} friends joined</span>
        <span>·</span>
        <span className="font-medium text-foreground">{stats.completed} bookings completed</span>
        <span>·</span>
        <span className="font-medium text-accent">{stats.pointsEarned} points earned</span>
      </div>
    </div>
  );
};

export default ReferralShare;
